import { Button } from '@mantine/core';
import { Icon, sizes, variants } from '@uag/react-core';
import { useTranslation } from 'react-i18next';

import type { PlanToIfcResponse } from 'app/modules/wallforming/components/plan-to-ifc/types/planToIfcTypes';

interface PlanToIfcResultCardProps {
    result: PlanToIfcResponse;
    onDownload: () => void;
    onEditWalls: () => void;
    onOpenSession: (sessionId: string) => void;
    onNewConversion: () => void;
}

/** Summary of a finished conversion; details are folded so the next actions stay visible. */
export const PlanToIfcResultCard = ({
    result,
    onDownload,
    onEditWalls,
    onOpenSession,
    onNewConversion
}: PlanToIfcResultCardProps) => {
    const { t } = useTranslation();
    const { specification, warnings } = result;

    return (
        <div className="space-y-2 rounded-md border border-border-light p-3" data-testid="plan-to-ifc-result">
            <div className="flex items-center gap-2 text-sm font-medium">
                <Icon icon="check_circle" style={{ color: '#16a34a' }} />
                {specification.name} ·{' '}
                {t('infoPlanToIfcResult', {
                    storeys: specification.storeys.length,
                    walls: specification.walls.length,
                    attempts: result.attempts
                })}
            </div>
            <div className="text-xs text-text-light">
                {specification.storeys
                    .map(
                        (storey) =>
                            `${storey.name} (${storey.elevation >= 0 ? '+' : ''}${storey.elevation.toFixed(2)} m)`
                    )
                    .join(' · ')}
            </div>
            {(specification.assumptions.length > 0 || warnings.length > 0) && (
                <details className="text-xs">
                    <summary className="cursor-pointer text-text-light">
                        {t('labelPlanToIfcDetails', {
                            assumptions: specification.assumptions.length,
                            warnings: warnings.length
                        })}
                    </summary>
                    <ul className="mt-1 max-h-28 list-disc overflow-auto pl-4">
                        {specification.assumptions.map((assumption) => (
                            <li key={`a-${assumption}`}>{assumption}</li>
                        ))}
                        {warnings.map((warning) => (
                            <li key={`w-${warning}`} style={{ color: '#b45309' }}>
                                {warning}
                            </li>
                        ))}
                    </ul>
                </details>
            )}
            <div className="text-xs text-text-light">{t('infoPlanToIfcReview')}</div>
            <div className="flex flex-wrap items-center gap-2">
                <Button
                    disabled={!result.sessionId}
                    leftSection={<Icon aria-hidden="true" icon="view_in_ar" />}
                    size={sizes.button.small}
                    onClick={() => result.sessionId && onOpenSession(result.sessionId)}
                >
                    {t('buttonStartWallForming')}
                </Button>
                <Button
                    leftSection={<Icon aria-hidden="true" icon="architecture" />}
                    size={sizes.button.small}
                    variant={variants.button.secondary}
                    onClick={onEditWalls}
                >
                    {t('buttonEditWalls')}
                </Button>
                <Button
                    leftSection={<Icon aria-hidden="true" icon="download" />}
                    size={sizes.button.small}
                    variant={variants.button.secondary}
                    onClick={onDownload}
                >
                    {t('buttonDownloadIfc')}
                </Button>
                <Button className="ml-auto" size={sizes.button.small} variant="subtle" onClick={onNewConversion}>
                    {t('buttonNewConversion')}
                </Button>
            </div>
        </div>
    );
};
